import { isAxiosError } from 'axios';
import { makeAutoObservable } from 'mobx';

import type { ProductAPI } from '~/api/Catalog';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { bottomNavigationStore } from '~/stores/BottomNavigationStore';
import type { Offer } from '~/stores/CategoriesStore/Offer';
import {
  getProductById,
  type LoadProductParams,
  Product,
} from '~/stores/CategoriesStore/Product';
import { ReviewsStore } from '~/stores/ReviewsStore';
import { Task } from '~/stores/shared/Task';
import { PreloadCache } from '~/utils/preloadCache';

export class ProductState {
  private readonly id: string | number;
  public readonly reviews: ReviewsStore;

  private product: Nullable<Product> = null;
  private offer: Nullable<Offer> = null;

  private static preloadedData = new PreloadCache<ProductAPI>({
    max: 500,
    ttlMs: 60_000,
  });

  constructor(id: string | number) {
    this.id = id;
    this.reviews = new ReviewsStore(id);

    this.init();
    makeAutoObservable(this);
  }

  public static async preload(params: LoadProductParams, serverLang?: string) {
    const { data } = await getProductById(params, serverLang);

    ProductState.preloadedData.set(String(params.productId), data);

    let reviews: Awaited<ReturnType<typeof ReviewsStore.preload>> | undefined;

    try {
      reviews = await ReviewsStore.preload(data.id);
    } catch {
      // Reviews will be fetched client-side as fallback
    }

    return {
      product: data,
      reviews,
    };
  }

  public static removePreloadedData(productId: string | number) {
    this.preloadedData.delete(String(productId));
  }

  public get data() {
    return this.product;
  }

  public get selectedOffer() {
    return this.offer;
  }

  public get notFound() {
    if (typeof window === 'undefined') {
      return !this.product;
    }

    return (
      isAxiosError(this.request.lastError) &&
      this.request.lastError.response?.status === 404 &&
      !this.request.isLoading
    );
  }

  public readonly request = new Task(async (params: LoadProductParams) => {
    const { data } = await getProductById(params);

    this.applyProduct(data);
  });

  public selectOffer(code: string) {
    const product = this.product;

    if (!product) {
      throw new Error('Product not found');
    }

    const offer = product.offers.find(({ sku }) => sku === code);

    if (!offer) {
      throw new Error('Offer not found');
    }

    this.offer = offer;
    analyticsService.track(AnalyticsEvent.SELECT_ITEM, offer);

    return this.offer;
  }

  private init() {
    const product =
      typeof window === 'undefined'
        ? ProductState.preloadedData.get(String(this.id))
        : window.__INITIAL_STATE__?.product;

    if (product) {
      if (Product.compareId(this.id, product)) {
        this.applyProduct(product);
        bottomNavigationStore.setProductState(this);
      }
    }
  }

  private applyProduct(data: ProductAPI) {
    this.product = new Product(data);
    const offer = this.product.offers[0];

    this.reviews.setId(data.id);

    if (offer) {
      this.selectOffer(offer.sku);
    }
  }
}
